import type { TileSettings } from '../types/tileBuilderTypes';

type TooltipMode = 'auto' | 'labelValue' | 'seriesValue' | 'seriesLabelValue';
type DotMode = 'full' | 'before' | 'after';

function escHtml(x: any) {
  return String(x)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function normalizeDotNotation(input: any, dotMode: DotMode) {
  const s = String(input ?? '');
  if (!s.includes('.')) return s;
  if (dotMode === 'before') return s.split('.')[0];
  if (dotMode === 'after') return s.split('.').slice(-1)[0];
  return s;
}

export type HarborTooltipExtractors = {
  getSeries: (e: any) => any;
  getLabel: (e: any) => any;
  getValue: (e: any) => any;
};

export function buildHarborItemTooltip(
  settings: TileSettings,
  extractors: HarborTooltipExtractors,
  opts?: { dotMode?: DotMode }
) {
  const dotMode: DotMode = opts?.dotMode ?? 'before';

  return (e: any): string | false => {
    if (settings.isInteractive === false) return false;

    const mode = (settings.tooltipMode ?? 'auto') as TooltipMode;
    const effectiveMode: TooltipMode = mode === 'auto' ? 'seriesLabelValue' : mode;

    const defaultVisibility =
      effectiveMode === 'labelValue'
        ? { series: false, label: true, value: true }
        : effectiveMode === 'seriesValue'
          ? { series: true, label: false, value: true }
          : { series: true, label: true, value: true };

    const showSeries =
      settings.tooltipShowSeries === undefined ? defaultVisibility.series : settings.tooltipShowSeries;
    const showLabel =
      settings.tooltipShowLabel === undefined ? defaultVisibility.label : settings.tooltipShowLabel;
    const showValue =
      settings.tooltipShowValue === undefined ? defaultVisibility.value : settings.tooltipShowValue;

    const seriesTitle = settings.tooltipSeriesTitle ?? 'Series';
    const labelTitle = settings.tooltipLabelTitle ?? 'Label';
    const valueTitle = settings.tooltipValueTitle ?? 'Value';

    const rawSeries = extractors.getSeries(e);
    const rawLabel = extractors.getLabel(e);
    const rawValue = extractors.getValue(e);

    const series = normalizeDotNotation(rawSeries, dotMode);
    // Optional: show only the trailing segment of a composite label (e.g. a `Group · Status` slice
    // whose label must stay unique for slice identity, but should read as just "Status" on hover).
    const labelDelimiter = (settings as any).tooltipLabelDelimiter as string | undefined;
    const label =
      labelDelimiter && typeof rawLabel === 'string' && rawLabel.includes(labelDelimiter)
        ? rawLabel.slice(rawLabel.lastIndexOf(labelDelimiter) + labelDelimiter.length)
        : rawLabel ?? '';
    const value = rawValue ?? '';

    const rows: Array<[string, any]> = [];
    if (showSeries) rows.push([seriesTitle, series]);
    if (showLabel) rows.push([labelTitle, label]);
    if (showValue) rows.push([valueTitle, value]);

    if (!rows.length) return false;

    return `
      <div style="padding:8px;">
        ${rows
          .map(([k, v]) => `${escHtml(k)}: <strong>${escHtml(v)}</strong>`)
          .join('<br/>')}
      </div>
    `;
  };
}